import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import { eur, num, dec, shortDate } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Truck, MapPin, User, Receipt, Building2, PackagePlus, Pencil, FileText, Printer } from "lucide-react";
import { openPdf } from "@/lib/pdf";

const Field = ({ label, value }) => (
  <div><div className="text-[11px] uppercase tracking-wide text-[#6a6d70]">{label}</div>
    <div className="mt-0.5 text-sm text-[#32363a]">{value || "—"}</div></div>
);

const PartnerCard = ({ icon: Icon, title, p, accent }) => (
  <div className="rounded-lg border border-[#e5e9f0] bg-white p-4">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: accent }}>
      <Icon size={14} /> {title}
    </div>
    <div className="mt-2 text-sm font-medium text-[#32363a]">{p?.name}</div>
    <div className="font-mono text-[10px] text-[#6a6d70]">{p?.partner_code}</div>
    {p?.street && <div className="mt-1 text-xs text-[#454b52]">{p.street}<br />{p.postal_code} {p.city}, {p.country}</div>}
  </div>
);

export default function SalesOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);

  const load = () => api.get(`/sales-orders/${id}`).then((r) => setO(r.data)).catch(() => setO(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (o === null) return <Loader label="Loading sales order…" />;
  if (o === false) return <div className="p-8 text-[#6a6d70]">Order not found.</div>;

  const nextStatus = { OPEN: "IN_DELIVERY", IN_DELIVERY: "DELIVERED", DELIVERED: "INVOICED" };
  const advance = async () => {
    const next = nextStatus[o.status];
    if (!next) return;
    await api.patch(`/sales-orders/${o.id}/status?status=${next}`);
    toast.success(`${o.order_no} → ${next.replace(/_/g, " ")}`);
    load();
  };

  const items = o.items || [];
  const conditions = items[0]?.conditions || [];
  const pf = o.partners || {};

  const printSo = async () => {
    try {
      await api.post("/print/send", { doc_type: "sales-order", ref: o.order_no, copies: 1 });
      toast.success(`Sales order ${o.order_no} sent to printer`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Print failed — set a Sales Order printer in Print Centre"); }
  };

  const createDelivery = async () => {    try {
      await api.post("/deliveries", { order_no: o.order_no });
      toast.success(`Delivery created for ${o.order_no}`);
      navigate("/deliveries");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create delivery"); }
  };
  const canDeliver = ["OPEN", "IN_DELIVERY"].includes(o.status);
  const canEdit = !["DELIVERED", "INVOICED"].includes(o.status);
  const bill = async () => {
    try {
      const { data } = await api.post(`/sales-orders/${o.id}/bill`);
      toast.success(`Invoice ${data.inv_no} posted · ${eur(data.gross)} to AR`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Billing failed"); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/sales")} data-testid="so-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-[#0a6ed1] hover:underline"><ArrowLeft size={16} /> Sales Orders</button>

      {/* Object page header */}
      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight text-[#32363a]">Sales Order {o.order_no}</h1>
              <StatusBadge status={o.status} />
            </div>
            <p className="mt-1 text-sm text-[#6a6d70]">{o.customer_name} · {o.customer_code}</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right"><div className="text-[11px] uppercase text-[#6a6d70]">Net Value</div><div className="text-2xl font-semibold text-[#107e3e]">{eur(o.net_value)}</div></div>
            <button data-testid="so-pdf-btn" onClick={() => openPdf(`/sales-orders/${o.id}/pdf`)}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#32363a] hover:border-[#D0313A]"><FileText size={16} /> PDF</button>
            <button data-testid="so-print-btn" onClick={printSo}
              className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#0f766e] hover:border-[#0f766e]"><Printer size={16} /> Print</button>
            {o.invoice_no
              ? <button data-testid="so-invoice-pdf-btn" onClick={() => openPdf(`/sales-orders/${o.id}/invoice/pdf`)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#107e3e] hover:border-[#107e3e]"><Receipt size={16} /> Invoice PDF</button>
              : <button data-testid="so-bill-btn" onClick={bill}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#107e3e] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c6a34]"><Receipt size={16} /> Create Invoice (EPS-301)</button>}
            {canEdit && (
              <button data-testid="so-edit-btn" onClick={() => navigate(`/sales/${o.id}/edit`)}
                className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#32363a] hover:border-[#8B5CF6]"><Pencil size={16} /> Edit</button>)}
            {canDeliver && (
              <button data-testid="so-create-delivery" onClick={createDelivery}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0EA5E9] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0284c7]"><PackagePlus size={16} /> Create Delivery</button>)}
            {nextStatus[o.status] && (
              <button data-testid="so-detail-advance" onClick={advance}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#8B5CF6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7c3aed]"><Truck size={16} /> Progress</button>)}
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6 border-t border-[#eff1f4] pt-4">
          <Field label="Sales Org" value={o.sales_org} />
          <Field label="Distribution" value={o.distribution_channel} />
          <Field label="Division" value={o.division} />
          <Field label="Incoterms" value={o.incoterms} />
          <Field label="Payment Terms" value={o.payment_terms} />
          <Field label="Req. Delivery" value={shortDate(o.req_delivery)} />
        </div>
      </div>

      {/* Partner functions */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Partner Functions</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <PartnerCard icon={User} title="Sold-To Party" p={pf.sold_to} accent="#0a6ed1" />
          <PartnerCard icon={MapPin} title="Ship-To Party" p={pf.ship_to} accent="#e9730c" />
          <PartnerCard icon={Receipt} title="Bill-To Party" p={pf.bill_to} accent="#7c3aed" />
          <PartnerCard icon={Building2} title="Payer" p={pf.payer} accent="#107e3e" />
        </div>
      </div>

      {/* Items */}
      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Order Items</h2>
        <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm" data-testid="so-items-table">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-[#e5e9f0] bg-[#f7f8fa] text-[11px] uppercase tracking-wide text-[#6a6d70]">
              <th className="px-4 py-2.5">Item</th><th className="px-4 py-2.5">Material</th><th className="px-4 py-2.5">Plant</th>
              <th className="px-4 py-2.5">Qty</th><th className="px-4 py-2.5">Unit Price</th><th className="px-4 py-2.5">Net Value</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.item_no} className="border-b border-[#eff1f4] last:border-0">
                  <td className="px-4 py-2.5 font-mono text-xs text-[#6a6d70]">{it.item_no}</td>
                  <td className="px-4 py-2.5"><div className="text-[#32363a]">{it.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{it.material_code}</div></td>
                  <td className="px-4 py-2.5 font-mono text-xs">{it.plant}</td>
                  <td className="px-4 py-2.5 font-mono">{num(it.qty)} {it.uom}</td>
                  <td className="px-4 py-2.5 font-mono">£{dec(it.unit_price, 3)}</td>
                  <td className="px-4 py-2.5 font-mono text-emerald-600">{eur(it.net_value)}</td>
                </tr>))}
              {items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#6a6d70]">No line items</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing conditions */}
      {conditions.length > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Pricing Conditions (Item 10)</h2>
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm" data-testid="so-conditions">
            {conditions.map((c) => (
              <div key={c.type} className="flex items-center justify-between border-b border-[#eff1f4] py-2 text-sm last:border-0">
                <span className="text-[#32363a]">{c.type}</span>
                <span className="font-mono text-xs text-[#6a6d70]">{c.rate}</span>
                <span className={`font-mono ${c.value < 0 ? "text-[#bb0000]" : "text-[#32363a]"}`}>{eur(c.value)}</span>
              </div>))}
          </div>
        </div>)}
    </div>
  );
}
